// Wayne State University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Wayne State Common Data Set 2025-2026 (PDF from the Office of
//    Institutional Research and Data Analytics): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C14 fall closing date, C21/C22 (no early decision, no early
//    action). Its check boxes were read on the rendered pages, not only the
//    text layer.
//  - Wayne State Admissions: First-year steps to apply (fee, test-optional
//    pathway, decision timing, December 1 merit scholarship priority,
//    auditions), Apply ("Our 2027-28 application is open!", fee waiver code
//    WSU2027 for fall 2027) and the Michigan Assured Admission Pact page.
//    Neither names a fall 2028 change.
//  - Wayne State Office of Student Financial Aid, Cost of attendance:
//    2026-27 estimated costs for first-year undergraduates (Michigan
//    resident and non-resident, living in Ghafari Hall). Used instead of
//    the CDS G1/G5, whose 2026-2027 figures carry cents and price tuition
//    and fees separately (first-year in-state tuition $14,188.34 plus
//    $2,298.34 fees); the aid office's own budget is whole dollars and its
//    items add up to its totals.
//  - Wayne State 2026-2027 Undergraduate Bulletin (majors file).
//  - College Scorecard, school id 172644 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Testing quirk: the admissions page makes the SAT or ACT optional through
// a test-optional pathway (short essay and activities list); the CDS C8
// marks the SAT or ACT "Required for some" for Fall 2027. The page shows
// test-optional, from the admissions page, and quotes the CDS mark.
// Deadline quirk: the admissions pages give no closing date; August 1 comes
// from the CDS C14 and the note says so.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Wayne State Common Data Set 2025-2026',
        url: 'https://irda.wayne.edu/common-data-set/2025-2026-cds.pdf',
        read: '2026-10-01',
    },
    process: {
        label: 'Wayne State Admissions, First-year steps to apply',
        url: 'https://wayne.edu/admissions/first-year/application-process',
        read: '2026-10-01',
    },
    apply: {
        label: 'Wayne State, Apply',
        url: 'https://wayne.edu/apply',
        read: '2026-10-01',
    },
    maap: {
        label: 'Wayne State Admissions, Michigan Assured Admission Pact (MAAP)',
        url: 'https://wayne.edu/admissions/maap',
        read: '2026-10-01',
    },
    costs: {
        label: 'Wayne State Financial Aid, Cost of attendance',
        url: 'https://wayne.edu/financial-aid/resources/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?172644-Wayne-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'wayne-state-university',
    kbId: 'wayne_state_university',
    name: 'Wayne State University',
    shortName: 'Wayne State',
    aliases: ['WSU'],
    location: 'Detroit, MI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (green and gold, from a wayne.edu FAQ page); decoration only.
    branding: {
        primary: '#0C5449',
        secondary: '#FFCC33',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/wayne_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 17263,
        admitted: 14430,
        enrolled: 3071,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Michigan)', applied: 15236, admitted: 12973 },
            { group: 'Out-of-state', applied: 1363, admitted: 1051 },
            { group: 'International', applied: 664, admitted: 406 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Wayne State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Wayne State has no early decision or early action plan. Its December 1 date is a priority date for merit scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Wayne State as a first-year student. Two things can change your path:',
        rows: [
            { school: 'Music, theatre and dance', requirement: 'You also schedule an audition or interview through the department.' },
            { school: 'Michigan Assured Admission Pact', requirement: 'Wayne State admits every Michigan high school graduate with a cumulative high school GPA of 3.0 or above (on a 4.0 scale). You still complete the application and the FAFSA.', source: 'maap' },
        ],
        source: 'process',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Fall closing date', value: 'August 1' },
        rows: [
            { plan: 'Merit scholarship priority', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Fall closing date', deadline: 'August 1', decision: null, binding: false },
        ],
        note: 'The fall 2027 application is open now, on Wayne State\'s own application or the Common App. A complete application by December 1 gets priority consideration for merit scholarships, with no separate scholarship application. Decisions usually arrive after October 1; if you apply after October 1, expect one within two to three weeks of sending everything. The admissions pages give no closing date; August 1 is the fall closing date in Wayne State\'s Common Data Set (section C14). The fee is $25, and fall 2027 applicants can waive it with the code WSU2027.',
        source: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SAT or ACT scores are optional. If you have not taken a test, or do not want scores considered, choose the test-optional pathway: answer one of two short essay prompts and send a full list of your leadership, extracurricular, community and work activities (a recommendation from an academic reference is optional). Common App materials may cover these. Going test-optional does not affect merit scholarship eligibility.',
        source: 'process',
        cdsLine: 'The Common Data Set marks the SAT or ACT as "Required for some" applicants for fall 2027, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (15 semester hours a term)', amounts: [16080, 34214] },
            { item: 'Food and housing (on campus, Ghafari Hall)', amounts: [12268, 12268] },
            { item: 'Books, supplies, materials and equipment', amounts: [1370, 1370] },
            { item: 'Transportation', amounts: [1846, 1846] },
            { item: 'Miscellaneous', amounts: [2538, 2538] },
            { item: 'Estimated loan fee', amounts: [82, 82] },
        ],
        totals: [34184, 52318],
        note: 'Estimates for a first-year undergraduate in a double room in Ghafari Hall, fall and winter semesters, at the lowest division tuition rates. The out-of-state column is Wayne State\'s non-resident estimate. Wayne State says this is not a bill, and your own figures are in the Financial Aid Portal.',
        source: 'costs',
        netPrice: {
            average: 11212,
            byIncome: [
                { income: '$0 to $30,000', amount: 9694 },
                { income: '$30,001 to $48,000', amount: 9087 },
                { income: '$48,001 to $75,000', amount: 9680 },
                { income: '$75,001 to $110,000', amount: 12492 },
                { income: 'Over $110,000', amount: 18584 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Wayne State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
